// VOY-417 §5 — Signals over time — DB-computed, NOT agent output.
//
// Four stats from getSignalStats over the trailing 12 months:
//   1. AVG TRIP SPEND ($) + booking count
//   2. SAVED BY VOY ($) — STUB until VOY-420 (pricing module)
//   3. TOP DESTINATION + visit count
//   4. SEARCH → BOOK rate (conversions / total searches)

import type { SignalStats } from '@/lib/profile'

export interface SignalsSectionProps {
  stats: SignalStats | null
  loading: boolean
}

function fmtMoney(amt: number | null | undefined, currency: string | null | undefined): string {
  if (amt == null) return '—'
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 0,
    }).format(amt)
  } catch {
    return `${Math.round(amt).toLocaleString()} ${currency || ''}`.trim()
  }
}

function fmtPct(ratio: number | null | undefined): string {
  if (ratio == null) return '—'
  return `${Math.round(ratio * 100)}%`
}

export function SignalsSection({ stats, loading }: SignalsSectionProps) {
  const s: SignalStats = stats ?? {
    avg_trip_spend: null,
    bookings_count: 0,
    saved_by_voy: null,
    top_destination: null,
    search_to_book_rate: null,
    total_searches: 0,
  }
  const noData = !loading
    && s.bookings_count === 0
    && s.total_searches === 0

  return (
    <section className="card" aria-labelledby="signals-h">
      <div className="card-h">
        <h3 id="signals-h">Signals over time</h3>
        <span className="meta">{loading ? 'LOADING…' : 'LAST 12 MO'}</span>
      </div>

      {noData ? (
        <div className="you-empty-state">
          <p className="you-empty-headline">No activity yet.</p>
          <p className="you-empty-sub">
            Once you start searching and booking, Voy starts charting the patterns here — average spend,
            top destinations, how often you actually book.
          </p>
        </div>
      ) : (
        <div className="you-signals-grid">
          <SignalRow
            label="AVG TRIP SPEND"
            value={fmtMoney(s.avg_trip_spend?.amount, s.avg_trip_spend?.currency)}
            sub={s.bookings_count > 0 ? `${s.bookings_count} booking${s.bookings_count === 1 ? '' : 's'}` : '—'}
          />
          <SignalRow
            label="SAVED BY VOY · PENDING"
            value="—"
            sub="VOY-420 ships the savings calc"
            stub
          />
          <SignalRow
            label="TOP DESTINATION"
            value={s.top_destination?.city ?? '—'}
            sub={s.top_destination
              ? `${s.top_destination.country_code ?? '—'} · ×${s.top_destination.visit_count}`
              : '—'}
          />
          <SignalRow
            label="SEARCH → BOOK"
            value={fmtPct(s.search_to_book_rate)}
            sub={`${s.bookings_count} of ${s.total_searches} searches`}
          />
        </div>
      )}
    </section>
  )
}

function SignalRow({
  label,
  value,
  sub,
  stub,
}: {
  label: string
  value: string
  sub: string
  stub?: boolean
}) {
  return (
    <div className={stub ? 'you-signal stub' : 'you-signal'}>
      <span className="lbl">{label}</span>
      <span className="num you-signal-value">{value}</span>
      <span className="you-signal-sub">{sub}</span>
    </div>
  )
}
